/**
 * @fileoverview Reloads the page on behalf of our own code, leaving a marker
 * so the next load can tell a reload we triggered from one the user started.
 * `pageLoadReport.ts` reads the marker and reports the reason.
 */

import {z} from 'zod';

/** sessionStorage key holding the pending app reload. */
const APP_RELOAD_KEY = 'cip_app_reload';

/**
 * How long a marker stays valid. Long enough for a "Leave site?" prompt to be
 * answered before the reload lands, short enough that a stale marker cannot
 * label a much later reload.
 */
export const APP_RELOAD_MARKER_MAX_AGE_MS = 2 * 60 * 1000;

/** Every reason our code reloads the page. */
export const APP_RELOAD_REASONS = [
  'version-update',
  'error-page',
  'fix-cache',
  'service-worker-missing',
  'concept-check-retry',
  'certificate-renamed',
  'teacher-certificate-created',
  'simteach-retry',
  'simteach-feedback-refresh',
  'math-datasets-failed',
  'math-assignment-copied',
  'stale-chunk',
  'restored-old-build',
  'stale-build-page-change',
] as const;

/** Why our code reloaded the page; one of {@link APP_RELOAD_REASONS}. */
export type AppReloadReason = (typeof APP_RELOAD_REASONS)[number];

/** Validates a stored reason. */
const AppReloadReasonSchema = z.enum(APP_RELOAD_REASONS);

/** The marker left in sessionStorage before an app reload. */
const AppReloadMarkerSchema = z.object({
  reason: AppReloadReasonSchema,
  markedAt: z.number(),
});

/**
 * Records why the page is reloading, then reloads it.
 *
 * @param params.reason - Why our code is reloading the page.
 * @param params.now - The time of the reload, for tests. Omitted → the current
 *   time.
 * @returns Nothing.
 * @throws Never — a storage failure is logged at debug level and the page
 *   still reloads, unlabeled.
 */
export function reloadPage({
  reason,
  now = new Date(),
}: {
  reason: AppReloadReason;
  now?: Date;
}): void {
  try {
    sessionStorage.setItem(
      APP_RELOAD_KEY,
      JSON.stringify({reason, markedAt: now.getTime()}),
    );
  } catch (e) {
    console.debug('[app-reload] recording the reload reason failed', e);
  }
  window.location.reload();
}

/**
 * Takes the marker {@link reloadPage} left, always removing it so it cannot
 * label a later load.
 *
 * The marker counts when `now - markedAt` lies in the closed interval
 * `[0, APP_RELOAD_MARKER_MAX_AGE_MS]`; both ends are inclusive. Known gap: a
 * user who declines a "Leave site?" prompt and then reloads by hand inside
 * that window has the manual reload labeled with the app's reason.
 *
 * @param params.now - The time of this load. Omitted → the current time.
 * @returns The reason, or null when there is no valid, recent marker.
 * @throws Never — storage failures are logged at debug level.
 */
export function takeAppReloadReason({
  now = new Date(),
}: {now?: Date} = {}): AppReloadReason | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(APP_RELOAD_KEY);
    sessionStorage.removeItem(APP_RELOAD_KEY);
  } catch (e) {
    console.debug('[app-reload] taking the reload reason failed', e);
    return null;
  }
  if (stored === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch (e) {
    console.debug('[app-reload] the reload reason is not JSON', e);
    return null;
  }
  const result = AppReloadMarkerSchema.safeParse(parsed);
  if (!result.success) {
    return null;
  }
  const ageMs = now.getTime() - result.data.markedAt;
  if (ageMs < 0 || ageMs > APP_RELOAD_MARKER_MAX_AGE_MS) {
    return null;
  }
  return result.data.reason;
}
